/**
 * @description Componente para listar pets relacionados ao tutor
 * @module VetPetList
 */

import { useState } from "react";
import {
  Stack,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Button,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Pets as PetsIcon,
  Add as AddIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from "@mui/icons-material";
import { FormButtonGroup } from "../../../../components/form";

interface IPet {
  id: string;
  name: string;
  species?: string;
  breed?: string;
  sex?: string;
  photoUrl?: string;
}

interface IProps {
  pets: IPet[];
  selectedPetId?: string;
  onSelectPet: (petId: string) => void;
  onAddNewPet: () => void;
  onGoBack: () => void;
  onContinue: () => void;
}

/**
 * Componente para listar pets do tutor
 * Permite selecionar um pet existente ou cadastrar um novo
 */
export function VetPetList({
  pets,
  selectedPetId,
  onSelectPet,
  onAddNewPet,
  onGoBack,
  onContinue,
}: IProps) {
  return (
    <Stack spacing={3}>
      {/* Título */}
      <Stack>
        <Typography variant="h6" fontWeight="600" color="neutral600.main">
          Selecione o Pet
        </Typography>
        <Typography variant="body1">
          {pets.length > 0
            ? "Escolha um pet cadastrado ou adicione um novo"
            : "Nenhum pet encontrado. Cadastre o primeiro pet deste tutor"}
        </Typography>
      </Stack>

      {/* Lista de pets */}
      {pets.length > 0 && (
        <Stack spacing={2}>
          {pets.map((pet) => (
            <Card
              key={pet.id}
              variant="outlined"
              sx={{
                borderColor:
                  selectedPetId === pet.id ? "primary.main" : "grey.300",
                borderWidth: selectedPetId === pet.id ? 2 : 1,
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 2,
                },
              }}
            >
              <CardActionArea onClick={() => onSelectPet(pet.id)}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Avatar do pet */}
                    <Avatar
                      src={pet.photoUrl}
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "primary.light",
                      }}
                    >
                      <PetsIcon />
                    </Avatar>

                    {/* Informações do pet */}
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="600">
                        {pet.name}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        {pet.species && (
                          <Chip
                            label={pet.species}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {pet.breed && (
                          <Chip
                            label={pet.breed}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {pet.sex && (
                          <Chip
                            icon={
                              pet.sex === "male" ? <MaleIcon /> : <FemaleIcon />
                            }
                            label={pet.sex === "male" ? "Macho" : "Fêmea"}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      {/* Botão para adicionar novo pet */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddNewPet}
        fullWidth
        sx={{ py: 1.5 }}
      >
        Cadastrar Novo Pet
      </Button>

      {/* Botões de navegação */}
      <FormButtonGroup
        goBackButtonText="Voltar"
        onGoBackButtonClick={onGoBack}
        nextButtonText="Continuar"
        onNextButtonClick={onContinue}
        disabled={!selectedPetId && pets.length > 0} // Desabilita se tiver pets mas nenhum selecionado
      />
    </Stack>
  );
}
